import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { X, RotateCw, Maximize2, Minimize2 } from 'lucide-react';

interface Viewer360Props {
  imageUrl: string;
  onClose: () => void;
  title?: string;
  /** Optional primary color for buttons */
  primaryColor?: string;
  /** When true, render only canvas + compact controls (no full-page chrome); parent provides card/wrapper */
  embedded?: boolean;
  /** When embedded, called when user clicks fullscreen so parent can open fullscreen overlay */
  onFullscreenRequest?: () => void;
  /** When embedded, optional class for the container (e.g. height) */
  className?: string;
}

const Viewer360: React.FC<Viewer360Props> = ({
  imageUrl,
  onClose,
  title = '360° View',
  primaryColor = '#FF3B30',
  embedded = false,
  onFullscreenRequest,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const targetRef = useRef(new THREE.Vector3(500, 0, 0));
  const animationFrameRef = useRef<number | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);

  const isDraggingRef = useRef(false);
  const previousMouseRef = useRef({ x: 0, y: 0 });
  const lonRef = useRef(0);
  const latRef = useRef(0);

  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const init = (): (() => void) | null => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w <= 0 || h <= 0) return null;
      if (rendererRef.current) return null;

      const scene = new THREE.Scene();
      sceneRef.current = scene;

      const camera = new THREE.PerspectiveCamera(
        55,
        w / h,
        0.1,
        1000
      );
      camera.position.set(0, 0, 0.1);
      cameraRef.current = camera;

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(w, h);
    renderer.setPixelRatio(window.devicePixelRatio);
      container.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      const geometry = new THREE.SphereGeometry(500, 60, 40);
      geometry.scale(-1, 1, 1);

      const textureLoader = new THREE.TextureLoader();
      textureLoader.setCrossOrigin('anonymous');
      textureLoader.load(
        imageUrl,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          const material = new THREE.MeshBasicMaterial({ map: texture });
          const sphere = new THREE.Mesh(geometry, material);
          scene.add(sphere);
          setIsLoading(false);
        },
        undefined,
        (error) => {
          console.error('Error loading 360 image:', error);
          setIsLoading(false);
        }
      );

      const animate = () => {
        animationFrameRef.current = requestAnimationFrame(animate);

        if (autoRotate && !isDraggingRef.current) {
          lonRef.current += 0.1;
        }

        const lat = Math.max(-85, Math.min(85, latRef.current));
        const phi = THREE.MathUtils.degToRad(90 - lat);
        const theta = THREE.MathUtils.degToRad(lonRef.current);

        targetRef.current.set(
          500 * Math.sin(phi) * Math.cos(theta),
          500 * Math.cos(phi),
          500 * Math.sin(phi) * Math.sin(theta)
        );
        camera.lookAt(targetRef.current);
        renderer.render(scene, camera);
      };

      animate();

      const handleResize = () => {
        if (!container || !camera || !renderer) return;
        const rw = container.clientWidth;
        const rh = container.clientHeight;
        if (rw <= 0 || rh <= 0) return;
        camera.aspect = rw / rh;
        camera.updateProjectionMatrix();
        renderer.setSize(rw, rh);
      };

      window.addEventListener('resize', handleResize);

      const innerRo = new ResizeObserver(handleResize);
      innerRo.observe(container);

      const onMouseDown = (event: MouseEvent) => {
      event.preventDefault();
      isDraggingRef.current = true;
      previousMouseRef.current = { x: event.clientX, y: event.clientY };
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const deltaX = event.clientX - previousMouseRef.current.x;
      const deltaY = event.clientY - previousMouseRef.current.y;
      lonRef.current += deltaX * 0.25;
      latRef.current -= deltaY * 0.25;
      previousMouseRef.current = { x: event.clientX, y: event.clientY };
    };

    const onMouseUp = () => { isDraggingRef.current = false; };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 1) {
        event.preventDefault();
        isDraggingRef.current = true;
        previousMouseRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!isDraggingRef.current || event.touches.length !== 1) return;
      event.preventDefault();
      const deltaX = event.touches[0].clientX - previousMouseRef.current.x;
      const deltaY = event.touches[0].clientY - previousMouseRef.current.y;
      lonRef.current += deltaX * 0.25;
      latRef.current -= deltaY * 0.25;
      previousMouseRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    };

    const onTouchEnd = () => { isDraggingRef.current = false; };

    renderer.domElement.addEventListener('mousedown', onMouseDown);
    renderer.domElement.addEventListener('mousemove', onMouseMove);
    renderer.domElement.addEventListener('mouseup', onMouseUp);
    renderer.domElement.addEventListener('touchstart', onTouchStart, { passive: false });
    renderer.domElement.addEventListener('touchmove', onTouchMove, { passive: false });
    renderer.domElement.addEventListener('touchend', onTouchEnd);

      return () => {
        innerRo.disconnect();
        window.removeEventListener('resize', handleResize);
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        if (rendererRef.current && container && container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
        renderer.domElement.removeEventListener('mousedown', onMouseDown);
      renderer.domElement.removeEventListener('mousemove', onMouseMove);
      renderer.domElement.removeEventListener('mouseup', onMouseUp);
        renderer.domElement.removeEventListener('touchstart', onTouchStart);
        renderer.domElement.removeEventListener('touchmove', onTouchMove);
        renderer.domElement.removeEventListener('touchend', onTouchEnd);
        renderer.dispose();
        geometry.dispose();
        rendererRef.current = null;
        sceneRef.current = null;
        cameraRef.current = null;
      };
    };

    const doInit = () => {
      const cleanup = init();
      if (cleanup) cleanupRef.current = cleanup;
    };

    const raf = requestAnimationFrame(doInit);
    const resizeRo = new ResizeObserver(() => {
      if (container.clientWidth > 0 && container.clientHeight > 0 && !rendererRef.current) doInit();
    });
    resizeRo.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      resizeRo.disconnect();
      const c = cleanupRef.current;
      if (c) { c(); cleanupRef.current = null; }
    };
  }, [imageUrl, autoRotate]);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const resetView = () => {
    lonRef.current = 0;
    latRef.current = 0;
  };

  const handleFullscreenClick = () => {
    if (embedded && onFullscreenRequest) onFullscreenRequest();
    else toggleFullscreen();
  };

  const containerEl = (
    <div ref={containerRef} className={embedded ? `relative bg-black w-full h-full min-h-[360px] min-w-[400px] ${className ?? ''}` : 'flex-1 relative bg-black min-h-0'}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-xl">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 rounded-full animate-spin" style={{ borderColor: primaryColor, borderTopColor: 'transparent' }} />
            <p className="text-slate-400 text-sm">Loading 360° view...</p>
          </div>
        </div>
      )}
    </div>
  );

  const controls = (
    <div className="flex items-center gap-1">
      <button type="button" onClick={resetView} className="p-2 text-slate-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors" title="Reset view">
        <RotateCw size={18} />
      </button>
      <button
        type="button"
        onClick={() => setAutoRotate(!autoRotate)}
        className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${autoRotate ? 'text-white' : 'text-slate-400 hover:bg-white/10'}`}
        style={autoRotate ? { backgroundColor: primaryColor } : undefined}
      >
        Auto Rotate
      </button>
      <button type="button" onClick={handleFullscreenClick} className="p-2 text-slate-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors" title={embedded ? 'Fullscreen' : isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
        <Maximize2 size={18} />
      </button>
      {!embedded && (
        <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-black/30 transition-colors" title="Close">
          <X size={20} />
        </button>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className="flex flex-col w-full h-full flex-1">
        <div className="relative w-full flex-1 min-h-[300px]">
          {containerEl}
          {!isLoading && (
            <>
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none">
                <span className="text-xs text-white/70">Drag to look around</span>
              </div>
              <div className="absolute top-2 right-2 pointer-events-auto flex items-center gap-1 rounded-lg bg-black/50 p-1 backdrop-blur-sm">
                {controls}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
      <div className="h-14 sm:h-16 shrink-0 flex items-center justify-between px-4 sm:px-6 border-b" style={{ backgroundColor: '#2F2F2F', borderColor: '#3d3d3d' }}>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="text-sm font-semibold text-white">{title}</span>
          <span className="text-xs text-slate-400 hidden sm:inline">Drag to explore</span>
        </div>
        {controls}
      </div>
      {containerEl}
      {!isLoading && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-xs text-slate-300 bg-black/50 pointer-events-none">
          🖱️ Drag to look around • 📱 Swipe on mobile
        </div>
      )}
    </div>
  );
};

export default Viewer360;
